import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import {
  ArrowUpDown,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Sparkles,
  MoreHorizontal,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

// Format dates consistently
const formatDate = (dateString?: string) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export type CompOff = {
  id: string; // derived from _id for DataTable
  _id: string;
  employee?: {
    _id?: string;
    fullName?: string;
    email?: string;
  };
  employeeName?: string;
  employeeEmail?: string;
  workDate: string;
  reason: string;
  hoursWorked: number;
  creditDays: number;
  status: "Pending" | "Approved" | "Rejected" | "Exhausted";
  requestedDate?: string;
  approvedDate?: string;
  expiryDate?: string;
  appliedDate?: string; // fallback alias from API
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "Approved":
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
          <CheckCircle className="mr-1 h-3 w-3" />
          Approved
        </Badge>
      );
    case "Rejected":
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">
          <XCircle className="mr-1 h-3 w-3" />
          Rejected
        </Badge>
      );
    case "Exhausted":
      return (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">
          <Clock className="mr-1 h-3 w-3" />
          Exhausted
        </Badge>
      );
    default:
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      );
  }
};

const AdminActionsCell = ({
  compOff,
  onApprove,
  onReject,
  onView,
}: {
  compOff: CompOff;
  onApprove: (compOff: CompOff) => void;
  onReject: (compOff: CompOff) => void;
  onView: (compOff: CompOff) => void;
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => onView(compOff)}>
          <Eye className="mr-2 h-4 w-4" />
          View Details
        </DropdownMenuItem>
        {compOff.status === "Pending" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onApprove(compOff)}
              className="text-green-600"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Approve
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onReject(compOff)}
              className="text-red-600"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Reject
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const EmployeeActionsCell = ({
  compOff,
  onView,
}: {
  compOff: CompOff;
  onView: (compOff: CompOff) => void;
}) => {
  return (
    <Button variant="outline" size="sm" onClick={() => onView(compOff)}>
      <Eye className="mr-2 h-4 w-4" />
      View
    </Button>
  );
};

export const createAdminCompOffColumns = (
  onApprove: (compOff: CompOff) => void,
  onReject: (compOff: CompOff) => void,
  onView: (compOff: CompOff) => void
): ColumnDef<CompOff>[] => [
  {
    accessorKey: "employeeName",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Employee
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const name =
        row.original.employee?.fullName || row.original.employeeName || "N/A";
      return <div className="text-left font-medium">{name}</div>;
    },
  },
  // {
  //   accessorKey: "employeeEmail",
  //   header: ({ column }) => (
  //     <Button
  //       variant="ghost"
  //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
  //       className="h-auto p-0 justify-start"
  //     >
  //       Email
  //       <ArrowUpDown className="ml-2 h-4 w-4" />
  //     </Button>
  //   ),
  //   cell: ({ row }) => {
  //     const email =
  //       row.original.employee?.email || row.original.employeeEmail || "—";
  //     return <div className="text-left text-muted-foreground">{email}</div>;
  //   },
  // },
  {
    accessorKey: "workDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Worked On
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-left">{formatDate(row.original.workDate)}</div>
    ),
  },
  {
    accessorKey: "creditDays",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Credit Days
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-left font-medium">{row.original.creditDays}</div>
    ),
  },
  {
    accessorKey: "hoursWorked",
    header: "Hours",
    cell: ({ row }) => (
      <div className="text-left">{row.original.hoursWorked}</div>
    ),
  },
  {
    accessorKey: "expiryDate",
    header: "Expires",
    cell: ({ row }) => (
      <div className="text-left">
        {row.original.expiryDate ? (
          formatDate(row.original.expiryDate)
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>
    ),
  },
  {
    accessorKey: "requestedDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Requested
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-left">
        {row.original.requestedDate || row.original.appliedDate
          ? formatDate(row.original.requestedDate || row.original.appliedDate)
          : "—"}
      </div>
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Status
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-left">{getStatusBadge(row.original.status)}</div>
    ),
  },
  {
    accessorKey: "requestedDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Requested
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-left">
        {row.original.requestedDate
          ? formatDate(row.original.requestedDate)
          : "—"}
      </div>
    ),
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <AdminActionsCell
        compOff={row.original}
        onApprove={onApprove}
        onReject={onReject}
        onView={onView}
      />
    ),
  },
];

export const createEmployeeCompOffColumns = (
  onView: (compOff: CompOff) => void
): ColumnDef<CompOff>[] => [
  {
    accessorKey: "workDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Worked On
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-left">{formatDate(row.original.workDate)}</div>
    ),
  },
  {
    accessorKey: "creditDays",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Credit Days
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-left font-medium">{row.original.creditDays}</div>
    ),
  },
  {
    accessorKey: "hoursWorked",
    header: "Hours",
    cell: ({ row }) => (
      <div className="text-left">{row.original.hoursWorked}</div>
    ),
  },
  {
    accessorKey: "expiryDate",
    header: "Expires",
    cell: ({ row }) => (
      <div className="text-left">
        {row.original.expiryDate ? (
          formatDate(row.original.expiryDate)
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Status
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-left">{getStatusBadge(row.original.status)}</div>
    ),
  },
  {
    accessorKey: "requestedDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Requested
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-left">
        {row.original.requestedDate || row.original.appliedDate
          ? formatDate(row.original.requestedDate || row.original.appliedDate)
          : "—"}
      </div>
    ),
  },
  {
    accessorKey: "reason",
    header: "Reason",
    cell: ({ row }) => (
      <div
        className="text-left max-w-[240px] truncate"
        title={row.original.reason}
      >
        {row.original.reason}
      </div>
    ),
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <EmployeeActionsCell compOff={row.original} onView={onView} />
    ),
  },
];
